import type { CreateSpaceRequest, SpaceDetail, SpaceKind, SpaceRole } from '@/types/spaces';

export const MAX_SPACE_PEOPLE = 6;
export const SPACE_NAME_SUGGESTIONS: Record<SpaceKind, string> = { house: 'Nossa Casa', trip: 'Nossa viagem', other: 'Nosso espaço' };
export type WizardStep = 1 | 2;
export type WizardPerson = { id: string; email: string; pct: string; role: SpaceRole };
export type WizardErrors = Record<string, string>;
export interface SpaceDraft {
  step: WizardStep;
  kind: SpaceKind;
  name: string;
  startsAt: string;
  endsAt: string;
  people: WizardPerson[];
  inviteEmail: string;
  equal: boolean;
  errors: WizardErrors;
  submitError: string | null;
}
export type WizardAction =
  | { type: 'kind'; kind: SpaceKind }
  | { type: 'field'; field: 'name' | 'startsAt' | 'endsAt' | 'inviteEmail'; value: string }
  | { type: 'person'; id: string; field: 'email' | 'pct'; value: string }
  | { type: 'person'; id: string; field: 'role'; value: SpaceRole }
  | { type: 'add'; id: string; creatorEmails: string[] }
  | { type: 'remove'; id: string }
  | { type: 'equal' }
  | { type: 'step'; step: WizardStep }
  | { type: 'errors'; errors: WizardErrors; step?: WizardStep; message?: string };

export function initialSpaceDraft(): SpaceDraft {
  return { step: 1, kind: 'house', name: SPACE_NAME_SUGGESTIONS.house, startsAt: '', endsAt: '',
    people: [{ id: 'creator', email: '', pct: '100', role: 'editor' }], inviteEmail: '', equal: true, errors: {}, submitError: null };
}

export function parsePercentage(value: string): number | null {
  if (!/^\d{1,3}(?:[.,]\d{1,2})?$/.test(value.trim())) return null;
  const number = Number(value.trim().replace(',', '.'));
  return number <= 100 ? number : null;
}

// A space's percentages are exactly what people type: nothing is rescaled. They add up to 100% (10000 hundredths)
// before anything is saved, and this is the one sum both the wizard and Configurações check and show.
export const FULL_SHARE = 10000;

/** The typed percentages' sum in hundredths of a percent, or null while any of them is not a valid percentage. */
export function percentageTotal(values: string[]): number | null {
  const parsed = values.map(parsePercentage);
  if (parsed.some((value) => value === null)) return null;
  return parsed.reduce((sum: number, value) => sum + Math.round((value as number) * 100), 0);
}

const HUNDREDTHS = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2, useGrouping: false });

/** "Total: 100%", "Total: 90% · faltam 10%", "Total: 110% · passou 10%", or "Total: —" while a value is invalid. */
export function percentageTotalLabel(total: number | null): string {
  if (total === null) return 'Total: —';
  const label = `Total: ${HUNDREDTHS.format(total / 100)}%`;
  if (total === FULL_SHARE) return label;
  const gap = HUNDREDTHS.format(Math.abs(FULL_SHARE - total) / 100);
  return total < FULL_SHARE ? `${label} · faltam ${gap}%` : `${label} · passou ${gap}%`;
}

// Match the API's largest-remainder split: hundredths, with ties going to later members.
export function normalizePercentages(values: number[]): number[] {
  const weights = values.map((value) => Math.round(value * 100));
  const sum = weights.reduce((total, value) => total + value, 0);
  if (!sum || values.some((value) => !Number.isFinite(value) || value < 0 || value > 100)) return [];
  const shares = weights.map((weight) => Math.floor(weight * 10000 / sum));
  const ordered = weights.map((weight, index) => ({ index, remainder: weight * 10000 % sum }))
    .sort((a, b) => b.remainder - a.remainder || b.index - a.index);
  const remaining = 10000 - shares.reduce((total, value) => total + value, 0);
  for (let i = 0; i < remaining; i++) shares[ordered[i].index]++;
  return shares.map((share) => share / 100);
}

function equalize(people: WizardPerson[]): WizardPerson[] {
  const percentages = normalizePercentages(people.map(() => 1));
  return people.map((person, index) => ({ ...person, pct: String(percentages[index]) }));
}

export function inviteEmailError(email: string, existing: string[]): string | null {
  const value = email.trim().toLowerCase();
  if (value.length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Informe um e-mail válido.';
  if (existing.some((item) => item.trim().toLowerCase() === value)) return 'Essa pessoa já está na lista.';
  return null;
}

export function spaceDraftReducer(state: SpaceDraft, action: WizardAction): SpaceDraft {
  const clean = { ...state, errors: {}, submitError: null };
  switch (action.type) {
    case 'kind': return { ...clean, kind: action.kind, step: 1,
      name: !state.name || state.name === SPACE_NAME_SUGGESTIONS[state.kind] ? SPACE_NAME_SUGGESTIONS[action.kind] : state.name };
    case 'field': return { ...clean, [action.field]: action.value };
    case 'step': return { ...clean, step: action.step };
    case 'errors': return { ...state, errors: action.errors, step: action.step ?? state.step, submitError: action.message ?? null };
    case 'person': return { ...clean, equal: action.field === 'pct' ? false : state.equal,
      people: state.people.map((person) => person.id !== action.id || (person.id === 'creator' && action.field !== 'pct') ? person
        : { ...person, [action.field]: action.value }) };
    case 'add': {
      const error = inviteEmailError(state.inviteEmail, [...action.creatorEmails, ...state.people.map((person) => person.email)]);
      if (error || state.people.length >= MAX_SPACE_PEOPLE) return { ...state, errors: { inviteEmail: error ?? 'O limite é de 6 pessoas, incluindo você.' } };
      const people = [...state.people, { id: action.id, email: state.inviteEmail.trim().toLowerCase(), pct: '0', role: 'editor' as const }];
      return { ...clean, people: state.equal ? equalize(people) : people, inviteEmail: '' };
    }
    case 'remove': {
      const people = state.people.filter((person) => person.id === 'creator' || person.id !== action.id);
      return { ...clean, people: state.equal ? equalize(people) : people };
    }
    case 'equal': return { ...clean, equal: true, people: equalize(state.people) };
  }
}

export function validateSpaceDraft(draft: SpaceDraft, step: WizardStep, creatorEmails: string[]): WizardErrors {
  const errors: WizardErrors = {};
  if (step === 1) {
    if (!draft.name.trim() || draft.name.trim().length > 80) errors.name = 'Dê um nome com até 80 caracteres.';
    if (draft.kind === 'trip') {
      if (!draft.startsAt) errors.startsAt = 'Informe o início da viagem.';
      if (!draft.endsAt) errors.endsAt = 'Informe o fim da viagem.';
      else if (draft.startsAt && draft.endsAt < draft.startsAt) errors.endsAt = 'O fim precisa ser no mesmo dia ou depois do início.';
    }
  }
  if (step === 2) {
    const emails = [...creatorEmails];
    for (const person of draft.people) {
      const pct = parsePercentage(person.pct);
      if (pct === null) errors[`pct-${person.id}`] = 'Use um percentual de 0 a 100, com até 2 casas decimais.';
      // The creator is the only active member until someone accepts an invite.
      else if (person.id === 'creator' && pct === 0) errors[`pct-${person.id}`] = 'Sua participação inicial precisa ser maior que zero.';
      if (person.id !== 'creator') {
        const error = inviteEmailError(person.email, emails);
        if (error) errors[`email-${person.id}`] = error;
        emails.push(person.email);
      }
    }
    const total = percentageTotal(draft.people.map((person) => person.pct));
    if (total !== null && total !== FULL_SHARE) errors['pct-total'] = 'Os percentuais precisam somar 100%.';
    if (draft.inviteEmail.trim()) errors.inviteEmail = 'Adicione esta pessoa à lista ou limpe o campo para continuar.';
  }
  return errors;
}

// Sent as typed: validation already required the parts to add up to 100%.
export function spaceCreationRequest(draft: SpaceDraft): CreateSpaceRequest {
  const percentages = draft.people.map((person) => parsePercentage(person.pct) ?? 0);
  return { name: draft.name.trim(), kind: draft.kind, model: 'split',
    startsAt: draft.kind === 'trip' ? `${draft.startsAt}T00:00:00.000Z` : null,
    endsAt: draft.kind === 'trip' ? `${draft.endsAt}T00:00:00.000Z` : null,
    myPct: percentages[0], invites: draft.people.slice(1).map((person, index) => ({ email: person.email.trim().toLowerCase(), pct: percentages[index + 1], role: person.role })) };
}

export function spaceInviteErrors(people: WizardPerson[], message: string): WizardErrors {
  const errors: WizardErrors = {};
  const normalizedMessage = message.toLowerCase();
  for (const person of people) {
    const email = person.email.trim().toLowerCase();
    if (person.id !== 'creator' && email && normalizedMessage.includes(email)) errors[`email-${person.id}`] = message;
  }
  return errors;
}

export interface SpaceCreatedEvents {
  created: { kind: SpaceKind; members: number; model: SpaceDetail['model'] };
  invites: { source: 'creation'; role: SpaceRole }[];
}

/**
 * What creating a space tells the activation funnel (VER-421): one `space.created`, and one `space.invite.sent` per
 * person invited along with it (the creator is the first of `people`). Kind and model come from what the API
 * created, not from the draft. No name, no e-mail, no percentage.
 */
export function spaceCreatedEvents(space: Pick<SpaceDetail, 'kind' | 'model'>, draft: Pick<SpaceDraft, 'people'>): SpaceCreatedEvents {
  return {
    created: { kind: space.kind, members: draft.people.length, model: space.model },
    invites: draft.people.slice(1).map((person) => ({ source: 'creation' as const, role: person.role })),
  };
}
